import React, { useEffect, useMemo, useRef, useState } from "react"
import { Box, Typography, IconButton, TextField, InputAdornment, Chip, Button } from "@mui/material"
import ExpandMoreIcon from "@mui/icons-material/ExpandMore"
import SearchIcon from "@mui/icons-material/Search"
import CloseIcon from "@mui/icons-material/Close"
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft"
import ChevronRightIcon from "@mui/icons-material/ChevronRight"
import CalendarTodayOutlinedIcon from "@mui/icons-material/CalendarTodayOutlined"
import DraggableRecipeCard from "./DraggableRecipeCard"
import RecipeCard from "./RecipeCard"
import CheckboxFilterPopover from "./CheckboxFilterPopover"
import { COLORS, getProdDayFilterWindow } from "../../../../utils"

const formatProdDay = (iso) => {
  const label = new Date(iso).toLocaleDateString("fr-FR", { weekday: "long", day: "numeric", month: "long" })
  return label.charAt(0).toUpperCase() + label.slice(1)
}

const getAssignedProdDates = (recipesBySlot, recipeId) => {
  const assigned = new Set()
  Object.values(recipesBySlot).forEach((placements) => {
    placements.forEach((p) => {
      if (p.recipe.recipeId === recipeId && p.prodDate) assigned.add(p.prodDate)
    })
  })
  return assigned
}

const isRefPlacedAnywhere = (recipesBySlot, recipeId) =>
  Object.values(recipesBySlot).some((placements) => placements.some((p) => p.recipe.recipeId === recipeId))

const isRefPendingAnywhere = (recipesBySlot, recipeId) =>
  Object.values(recipesBySlot).some((placements) => placements.some((p) =>
    p.recipe.recipeId === recipeId && p.prodDate === null && (p.recipe.prodDates || []).length > 1
  ))

const RecipeCatalogPanel = ({
  recipes, categoryOrder, deliveryDays, searchValue, onSearchChange, activeCat, onCatChange,
  selectedProdDays, onProdDaysChange, selectedCategoryTagIds, onCategoryTagIdsChange,
  selectedDishTypeTagIds, onDishTypeTagIdsChange,
  recipesBySlot, onClose, onRemoveRecipeEverywhere,
  onCardDragStart, onCardDragEnd,
}) => {
  const q = searchValue.trim().toLowerCase()

  const chipScrollRef = useRef(null)
  const [canScrollLeft, setCanScrollLeft] = useState(false)
  const [canScrollRight, setCanScrollRight] = useState(false)

  const updateChipScrollState = () => {
    const el = chipScrollRef.current
    if (!el) return
    setCanScrollLeft(el.scrollLeft > 1)
    setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 1)
  }

  const scrollChips = (direction) => {
    const el = chipScrollRef.current
    if (!el) return
    el.scrollBy({ left: direction * Math.round(el.clientWidth * 0.8), behavior: "smooth" })
  }

  const availableCategories = useMemo(() => [{ id: "all", label: "Tous" }, ...categoryOrder], [categoryOrder])

  useEffect(() => {
    updateChipScrollState()
  }, [availableCategories])

  const availableProdDays = useMemo(() => {
    const { start, end } = getProdDayFilterWindow(deliveryDays)
    const distinct = [...new Set(recipes.flatMap((r) => r.prodDates || []))]
    return distinct.filter((d) => { const date = new Date(d); return date >= start && date <= end }).sort()
  }, [recipes, deliveryDays])

  const availableCategoryTags = useMemo(() => {
    const byId = new Map()
    recipes.forEach((r) => (r.categoryTags || []).forEach((tag) => byId.set(tag.id, tag)))
    return [...byId.values()].sort((a, b) => a.name.localeCompare(b.name))
  }, [recipes])

  const availableDishTypeTags = useMemo(() => {
    const byId = new Map()
    recipes.forEach((r) => (r.dishTypeTags || []).forEach((tag) => byId.set(tag.id, tag)))
    return [...byId.values()].sort((a, b) => a.name.localeCompare(b.name))
  }, [recipes])

  const filtered = recipes
    .filter((r) => {
      const matchSearch = !q || (r.name || "").toLowerCase().includes(q) || (r.code || "").toLowerCase().includes(q)
      const matchCat = activeCat === "all" || r.cat === activeCat
      const matchProdDay = selectedProdDays.length === 0 || (r.prodDates || []).some((d) => selectedProdDays.includes(d))
      const matchCategoryTag = selectedCategoryTagIds.length === 0
        || (r.categoryTags || []).some((tag) => selectedCategoryTagIds.includes(tag.id))
      const matchDishTypeTag = selectedDishTypeTagIds.length === 0
        || (r.dishTypeTags || []).some((tag) => selectedDishTypeTagIds.includes(tag.id))
      return matchSearch && matchCat && matchProdDay && matchCategoryTag && matchDishTypeTag
    })
    .sort((a, b) => ((b.prodDates || []).length > 0 ? 1 : 0) - ((a.prodDates || []).length > 0 ? 1 : 0))

  const narrowByFilter = (dates) => (selectedProdDays.length > 0 ? dates.filter((d) => selectedProdDays.includes(d)) : dates)

  const catalogEntries = useMemo(() => filtered.flatMap((recipe) => {
    const totalDays = (recipe.prodDates || []).length
    const blocked = isRefPendingAnywhere(recipesBySlot, recipe.recipeId)

    if (totalDays <= 1) {
      const done = isRefPlacedAnywhere(recipesBySlot, recipe.recipeId)
      return [{ key: recipe.recipeId, recipe, displayProdDates: recipe.prodDates, isDone: done, isBlocked: blocked, draggable: !blocked }]
    }

    const assignedSet = getAssignedProdDates(recipesBySlot, recipe.recipeId)
    const remainingAll = recipe.prodDates.filter((d) => !assignedSet.has(d))
    const assignedAll = recipe.prodDates.filter((d) => assignedSet.has(d))
    const displayedRemaining = narrowByFilter(remainingAll)
    const displayedAssigned = narrowByFilter(assignedAll)

    if (assignedAll.length === 0) {
      if (displayedRemaining.length === 0) return []
      return [{ key: recipe.recipeId, recipe, displayProdDates: displayedRemaining, isDone: false, isBlocked: blocked, draggable: !blocked }]
    }
    if (remainingAll.length === 0) {
      if (displayedAssigned.length === 0) return []
      return [{ key: recipe.recipeId, recipe, displayProdDates: displayedAssigned, isDone: true, isBlocked: blocked, draggable: !blocked }]
    }
    const entries = []
    if (displayedRemaining.length > 0) {
      entries.push({ key: `${recipe.recipeId}-remaining`, recipe, displayProdDates: displayedRemaining, isDone: false, isBlocked: blocked, draggable: !blocked })
    }
    if (displayedAssigned.length > 0) {
      entries.push({ key: `${recipe.recipeId}-assigned`, recipe, displayProdDates: displayedAssigned, isDone: true, isBlocked: blocked, draggable: !blocked })
    }
    return entries
  }), [filtered, recipesBySlot, selectedProdDays])

  return (
    <Box className="flexColumn stretch overflowHidden" sx={sx.panel}>
      <Box className="flexRow center spaceBetween" sx={sx.header}>
        <Typography sx={sx.title}>Catalogue de plats</Typography>
        <IconButton size="large" onClick={onClose} aria-label="Fermer le panneau">
          <ChevronLeftIcon fontSize="large" />
          </IconButton>
      </Box>

      <Box className="flexColumn stretch overflowHidden flex1" sx={sx.body}>
        <Box className="flexColumn stretch shrink0" sx={sx.filters}>
          <TextField
            fullWidth
            size="small"
            placeholder="Rechercher un plat…"
            value={searchValue}
            onChange={(e) => onSearchChange(e.target.value)}
            sx={{ "& .MuiInputBase-root": { borderRadius: "6px" } }}
            InputProps={{
              startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment>,
              endAdornment: searchValue && (
                <InputAdornment position="end">
                  <IconButton size="large" onClick={() => onSearchChange("")} aria-label="Effacer la recherche">
                    <CloseIcon fontSize="large" />
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />

          <CheckboxFilterPopover
            icon={<CalendarTodayOutlinedIcon fontSize="small" />}
            label="Jour de prod"
            items={availableProdDays.map((day) => ({ id: day, label: formatProdDay(day) }))}
            selectedIds={selectedProdDays}
            onChange={onProdDaysChange}
            emptyMessage="Aucun jour de production"
            fullWidth
          />

          <Box sx={sx.chipRowWrapper}>
            <Box
              ref={chipScrollRef}
              onScroll={updateChipScrollState}
              className="flexRow"
              sx={sx.chipRow}
              role="group"
              aria-label="Catégories"
            >
              {availableCategories.map((c) => (
                <Chip
                  key={c.id}
                  label={c.label}
                  size="medium"
                  clickable
                  onClick={() => onCatChange(c.id)}
                  color={activeCat === c.id ? "primary" : "default"}
                  variant={activeCat === c.id ? "filled" : "outlined"}
                  sx={sx.chip}
                />
              ))}
            </Box>
            {canScrollLeft && (
              <IconButton size="small" onClick={() => scrollChips(-1)} sx={{ ...sx.chipArrow, ...sx.chipArrowLeft }} aria-label="Faire défiler vers la gauche">
                <ChevronLeftIcon fontSize="small" />
              </IconButton>
            )}
            {canScrollRight && (
              <IconButton size="small" onClick={() => scrollChips(1)} sx={{ ...sx.chipArrow, ...sx.chipArrowRight }} aria-label="Faire défiler vers la droite">
                <ChevronRightIcon fontSize="small" />
              </IconButton>
            )}
          </Box>

          <Box className="flexRow wrap" sx={sx.filterRow}>
            <Button variant="outlined" size="small" disabled endIcon={<ExpandMoreIcon />} title="Bientôt disponible" sx={sx.disabledPill}>
              Régime
            </Button>
            <CheckboxFilterPopover
              label="Type"
              items={availableDishTypeTags.map((tag) => ({ id: tag.id, label: tag.name }))}
              selectedIds={selectedDishTypeTagIds}
              onChange={onDishTypeTagIdsChange}
              emptyMessage="Aucun type de plat"
            />
            <CheckboxFilterPopover
              label="Catégorie"
              items={availableCategoryTags.map((tag) => ({ id: tag.id, label: tag.name }))}
              selectedIds={selectedCategoryTagIds}
              onChange={onCategoryTagIdsChange}
              emptyMessage="Aucune thématique de cuisine"
            />
          </Box>
        </Box>

        <Box className="flexColumn stretch overflowAuto flex1" sx={sx.results}>
          <Typography sx={sx.count}>RECETTES ({catalogEntries.length})</Typography>
          {catalogEntries.length === 0 && <Typography sx={sx.empty}>Aucune recette ne correspond à ces filtres.</Typography>}
          {catalogEntries.map((entry) => (
            entry.draggable ? (
              <DraggableRecipeCard
                key={entry.key}
                recipe={entry.recipe}
                displayProdDates={entry.displayProdDates}
                isDone={entry.isDone}
                onRemove={entry.isDone ? () => onRemoveRecipeEverywhere(entry.recipe.recipeId) : undefined}
                onDragStart={onCardDragStart}
                onDragEnd={onCardDragEnd}
              />
            ) : (
              <RecipeCard
                key={entry.key}
                recipe={entry.recipe}
                displayProdDates={entry.displayProdDates}
                isDone={entry.isDone}
                isBlocked={entry.isBlocked}
                onRemove={entry.isDone ? () => onRemoveRecipeEverywhere(entry.recipe.recipeId) : undefined}
              />
            )
          ))}
        </Box>
      </Box>
    </Box>
  )
}

export default RecipeCatalogPanel

const sx = {
  panel: { width: "100%", flex: 1, minHeight: 0, bgcolor: COLORS.WHITE },
  header: { p: "8px 8px 8px 16px", height: "56px", borderBottom: "1px solid", borderColor: COLORS.DEFAULT_GREY, flexShrink: 0 },
  title: { fontSize: 14, fontWeight: 500, color: COLORS.GREY_900 },
  body: { minHeight: 0 },
  filters: { gap: "12px", p: "16px" },
  chipRowWrapper: { position: "relative", width: "100%", minWidth: 0 },
  chipRow: {
    width: "100%", gap: "8px", flexWrap: "nowrap", overflowX: "auto", scrollbarWidth: "none",
    "&::-webkit-scrollbar": { display: "none" },
  },
  chip: { flexShrink: 0 },
  chipArrow: {
    position: "absolute", top: "50%", transform: "translateY(-50%)",
    width: "24px", height: "24px", bgcolor: COLORS.WHITE, border: "1px solid",
    borderColor: COLORS.DEFAULT_GREY, boxShadow: "0 1px 3px rgba(0,0,0,0.12)", zIndex: 1,
    "&:hover": { bgcolor: COLORS.LIGHT_GREY_2 },
  },
  chipArrowLeft: { left: "-2px" },
  chipArrowRight: { right: "-2px" },
  filterRow: { gap: "8px" },
  disabledPill: { borderRadius: "999px", textTransform: "none", fontSize: 13 },
  results: { gap: "8px", p: "0 16px 16px", minHeight: 0 },
  count: { fontSize: 12, fontWeight: 600, color: COLORS.DARK_GREY, mb: "2px" },
  empty: { textAlign: "center", p: "24px 12px", color: COLORS.DISABLED, fontSize: 12, lineHeight: 1.5 },
}
